import { useState, useMemo, useEffect } from 'react';
import {
  ExternalLink,
  Copy,
  Monitor,
  Settings,
  Eye,
  QrCode,
  Link2,
  Palette,
  Clock,
  ShoppingBag,
  Wallet,
  Store,
  Smartphone,
  Loader2,
  Check,
  Save,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useLocations, LocationSettings } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface KioskSettings {
  allowDineIn: boolean;
  allowTakeout: boolean;
  showPrices: boolean;
  requireName: boolean;
  timeoutSeconds: number;
  theme: string;
  enableWalletPayments: boolean;
  enableCashPayments: boolean;
}

const DEFAULT_KIOSK_SETTINGS: KioskSettings = {
  allowDineIn: true,
  allowTakeout: true,
  showPrices: true,
  requireName: false,
  timeoutSeconds: 120,
  theme: 'dark',
  enableWalletPayments: true,
  enableCashPayments: true,
};

export default function CustomerKioskPortal() {
  const { locations, updateLocation } = useLocations();
  const { organization } = useAuth();

  // Selected location for configuration
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('tablet');
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Configuration settings - loaded from selected location
  const [settings, setSettings] = useState<KioskSettings>(DEFAULT_KIOSK_SETTINGS);

  // Get the selected location object
  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  // Load settings from selected location when it changes
  useEffect(() => {
    if (selectedLocation) {
      const locationSettings = selectedLocation.settings as Record<string, any> | null;
      const kioskSettings = locationSettings?.kiosk as Partial<KioskSettings> | undefined;
      
      if (kioskSettings) {
        setSettings({
          ...DEFAULT_KIOSK_SETTINGS,
          ...kioskSettings,
        });
      } else {
        setSettings(DEFAULT_KIOSK_SETTINGS);
      }
      setHasChanges(false);
    }
  }, [selectedLocationId, selectedLocation?.settings]);

  // Update settings and mark as changed
  const updateSettings = (updates: Partial<KioskSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  // Save settings to database
  const handleSaveSettings = async () => {
    if (!selectedLocationId || !selectedLocation) {
      toast.error('No location selected', {
        description: 'Please select a location to save settings for.',
      });
      return;
    }

    setIsSaving(true);
    try {
      // Merge with existing location settings
      const existingSettings = (selectedLocation.settings as Record<string, any>) || {};
      const updatedSettings = {
        ...existingSettings,
        kiosk: settings,
      };

      await updateLocation.mutateAsync({
        id: selectedLocationId,
        updates: {
          settings: updatedSettings as LocationSettings,
        },
      });

      toast.success('Settings saved', {
        description: `Kiosk settings saved for ${selectedLocation.name}`,
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save kiosk settings:', error);
      toast.error('Failed to save', {
        description: 'Could not save kiosk settings. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Generate the kiosk URL
  const kioskUrl = useMemo(() => {
    if (!organization) return '';
    
    const baseUrl = window.location.origin;
    const orgSlug = organization.slug || organization.id;
    const selectedLocation = locations.find(l => l.id === selectedLocationId);
    const locCode = selectedLocation?.code || selectedLocationId;
    
    if (selectedLocationId && locCode) {
      return `${baseUrl}/customer-kiosk?org=${orgSlug}&loc=${locCode}`;
    }
    return `${baseUrl}/customer-kiosk?org=${orgSlug}`;
  }, [organization, selectedLocationId, locations]);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(kioskUrl);
    setCopied(true);
    toast.success('URL Copied', {
      description: 'Customer kiosk URL copied to clipboard',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  // Preview dimensions based on mode
  const previewDimensions = {
    desktop: { width: '100%', height: '600px' },
    tablet: { width: '768px', height: '600px' },
    mobile: { width: '375px', height: '667px' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold">Customer Kiosk Portal</h2>
        <p className="text-sm text-muted-foreground">
          Configure and preview the customer-facing self-service kiosk
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <Store className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{locations.length}</p>
                <p className="text-sm text-muted-foreground">Locations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Wallet className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">Enabled</p>
                <p className="text-sm text-muted-foreground">Wallet Payments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <ShoppingBag className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">Active</p>
                <p className="text-sm text-muted-foreground">Self-Service</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="portal" className="space-y-6">
        <TabsList>
          <TabsTrigger value="portal" className="gap-2">
            <Link2 className="h-4 w-4" />
            Portal Link
          </TabsTrigger>
          <TabsTrigger value="preview" className="gap-2">
            <Eye className="h-4 w-4" />
            Live Preview
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuration
          </TabsTrigger>
        </TabsList>

        {/* Portal Link Tab */}
        <TabsContent value="portal" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <QrCode className="h-4 w-4" />
                Kiosk Portal URL
              </CardTitle>
              <CardDescription>
                Use this URL to access the customer-facing kiosk on any device
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Location Selection */}
              <div className="space-y-2">
                <Label>Location (Optional)</Label>
                <Select
                  value={selectedLocationId}
                  onValueChange={setSelectedLocationId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All locations (customer selects)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All locations (customer selects)</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} {loc.code && `(${loc.code})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select a specific location to pre-configure the kiosk for that store
                </p>
              </div>

              {/* Generated URL */}
              <div className="space-y-2">
                <Label>Portal URL</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all">
                    {kioskUrl}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyUrl}
                    className="shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(kioskUrl, '_blank')}
                    className="shrink-0"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button variant="outline" className="gap-2" onClick={handleCopyUrl}>
                  <Copy className="h-4 w-4" />
                  Copy URL
                </Button>
                <Button 
                  variant="outline" 
                  className="gap-2"
                  onClick={() => window.open(kioskUrl, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in New Tab
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="text-sm text-muted-foreground space-y-3 list-decimal list-inside">
                <li>
                  <strong className="text-foreground">Deploy a Device:</strong>{' '}
                  Set up a tablet or touchscreen device at your location
                </li>
                <li>
                  <strong className="text-foreground">Configure Browser:</strong>{' '}
                  Open the browser in kiosk/fullscreen mode
                </li>
                <li>
                  <strong className="text-foreground">Navigate to URL:</strong>{' '}
                  Enter the portal URL or set it as the homepage
                </li>
                <li>
                  <strong className="text-foreground">Customer Login:</strong>{' '}
                  Customers can log in with their phone number and PIN to access their wallet
                </li>
                <li>
                  <strong className="text-foreground">Self-Service:</strong>{' '}
                  Customers browse menu, add items to cart, and pay with their wallet balance
                </li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Live Preview Tab */}
        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    Live Preview
                  </CardTitle>
                  <CardDescription>
                    See how the kiosk looks to customers
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Device Mode Toggle */}
                  <div className="flex items-center border rounded-lg p-1">
                    <Button
                      variant={previewMode === 'desktop' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setPreviewMode('desktop')}
                    >
                      <Monitor className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={previewMode === 'tablet' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setPreviewMode('tablet')}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="4" y="2" width="16" height="20" rx="2" />
                        <line x1="12" y1="18" x2="12" y2="18" strokeLinecap="round" />
                      </svg>
                    </Button>
                    <Button
                      variant={previewMode === 'mobile' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setPreviewMode('mobile')}
                    >
                      <Smartphone className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {showPreview ? (
                <div className="flex justify-center">
                  <div
                    className={cn(
                      'border rounded-lg overflow-hidden bg-background shadow-lg transition-all',
                      previewMode === 'mobile' && 'rounded-3xl'
                    )}
                    style={{
                      width: previewDimensions[previewMode].width,
                      height: previewDimensions[previewMode].height,
                      maxWidth: '100%',
                    }}
                  >
                    <iframe
                      src={kioskUrl}
                      className="w-full h-full border-0"
                      title="Customer Kiosk Preview"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Monitor className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <h3 className="font-medium mb-2">Preview Hidden</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Click "Show Preview" to see a live preview of the customer kiosk
                  </p>
                  <Button onClick={() => setShowPreview(true)}>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Preview
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="settings" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Location Selection for Settings */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  Select Location
                </CardTitle>
                <CardDescription>
                  Choose a location to configure kiosk settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedLocationId || 'none'}
                  onValueChange={(value) => setSelectedLocationId(value === 'none' ? '' : value)}
                >
                  <SelectTrigger className="max-w-sm">
                    <SelectValue placeholder="Select a location..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a location...</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} {loc.code && `(${loc.code})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {!selectedLocationId ? (
              <Card className="md:col-span-2">
                <CardContent className="py-12 text-center">
                  <Settings className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">
                    Select a location above to configure its kiosk settings
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Order Types */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShoppingBag className="h-4 w-4" />
                      Order Types
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Dine-In Orders</Label>
                        <p className="text-xs text-muted-foreground">
                          Allow customers to order for dining in
                        </p>
                      </div>
                      <Switch
                        checked={settings.allowDineIn}
                        onCheckedChange={(checked) => updateSettings({ allowDineIn: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Takeout Orders</Label>
                        <p className="text-xs text-muted-foreground">
                          Allow customers to order for takeout
                        </p>
                      </div>
                      <Switch
                        checked={settings.allowTakeout}
                        onCheckedChange={(checked) => updateSettings({ allowTakeout: checked })}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Display Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Display Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Show Prices</Label>
                        <p className="text-xs text-muted-foreground">
                          Display product prices on the kiosk
                        </p>
                      </div>
                      <Switch
                        checked={settings.showPrices}
                        onCheckedChange={(checked) => updateSettings({ showPrices: checked })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Theme</Label>
                      <Select
                        value={settings.theme}
                        onValueChange={(value) => updateSettings({ theme: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">Light</SelectItem>
                          <SelectItem value="dark">Dark</SelectItem>
                          <SelectItem value="system">System Default</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Customer Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Customer Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Require Customer Name</Label>
                        <p className="text-xs text-muted-foreground">
                          Ask customers for their name at checkout
                        </p>
                      </div>
                      <Switch
                        checked={settings.requireName}
                        onCheckedChange={(checked) => updateSettings({ requireName: checked })}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Session Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Session Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Inactivity Timeout (seconds)</Label>
                      <Input
                        type="number"
                        min="30"
                        max="600"
                        value={settings.timeoutSeconds}
                        onChange={(e) => updateSettings({ timeoutSeconds: parseInt(e.target.value) || 120 })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Time before the kiosk resets due to inactivity (30-600 seconds)
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Settings */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wallet className="h-4 w-4" />
                      Payment Options
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <Label>Wallet Payments</Label>
                          <p className="text-xs text-muted-foreground">
                            Allow customers to pay with their wallet balance
                          </p>
                        </div>
                        <Switch
                          checked={settings.enableWalletPayments}
                          onCheckedChange={(checked) => updateSettings({ enableWalletPayments: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <Label>Pay at Counter</Label>
                          <p className="text-xs text-muted-foreground">
                            Allow customers to pay cash at the counter
                          </p>
                        </div>
                        <Switch
                          checked={settings.enableCashPayments}
                          onCheckedChange={(checked) => updateSettings({ enableCashPayments: checked })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Save Button */}
          {selectedLocationId && (
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm font-medium">
                  {hasChanges ? 'You have unsaved changes' : 'Settings saved'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Configuring kiosk for: {selectedLocation?.name}
                </p>
              </div>
              <Button 
                onClick={handleSaveSettings} 
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

